import React, { useState, useRef, useEffect, useCallback } from 'react';
import './Tooltip.css';

interface TooltipProps {
  content: string;
  children: React.ReactNode;
  className?: string;
  position?: 'top' | 'bottom' | 'left' | 'right';
  trigger?: 'hover' | 'click' | 'both';
}

/**
 * Reusable tooltip component that shows content on hover and/or click
 */
export const Tooltip: React.FC<TooltipProps> = ({
  content,
  children,
  className = '',
  position = 'top',
  trigger = 'both'
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [clickVisible, setClickVisible] = useState(false);
  const [adjustedPosition, setAdjustedPosition] = useState(position);
  const [positionStyle, setPositionStyle] = useState<React.CSSProperties>({});
  const [isPositioned, setIsPositioned] = useState(false);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);

  // Calculate optimal position to keep tooltip within viewport
  const calculatePosition = useCallback(() => {
    if (!tooltipRef.current || !triggerRef.current || isPositioned) return;

    const tooltip = tooltipRef.current;
    const trigger = triggerRef.current;
    const triggerRect = trigger.getBoundingClientRect();
    const tooltipRect = tooltip.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    // Skip if tooltip doesn't have proper dimensions yet
    if (tooltipRect.width === 0 || tooltipRect.height === 0) return;

    let newPosition = position;
    let style: React.CSSProperties = {};
    let needsCustomPositioning = false;

    // Calculate space available in each direction
    const spaceTop = triggerRect.top;
    const spaceBottom = viewportHeight - triggerRect.bottom;
    const spaceLeft = triggerRect.left;
    const spaceRight = viewportWidth - triggerRect.right;

    // Determine best position based on available space
    if (position === 'top' && spaceTop < tooltipRect.height + 10) {
      if (spaceBottom > tooltipRect.height + 10) {
        newPosition = 'bottom';
      }
    } else if (position === 'bottom' && spaceBottom < tooltipRect.height + 10) {
      if (spaceTop > tooltipRect.height + 10) {
        newPosition = 'top';
      }
    } else if (position === 'left' && spaceLeft < tooltipRect.width + 10) {
      if (spaceRight > tooltipRect.width + 10) {
        newPosition = 'right';
      }
    } else if (position === 'right' && spaceRight < tooltipRect.width + 10) {
      if (spaceLeft > tooltipRect.width + 10) {
        newPosition = 'left';
      }
    }

    // Only apply custom positioning if tooltip would be clipped horizontally
    if (newPosition === 'top' || newPosition === 'bottom') {
      const triggerCenter = triggerRect.left + triggerRect.width / 2;
      const tooltipHalfWidth = tooltipRect.width / 2;
      const idealLeft = triggerCenter - tooltipHalfWidth;
      
      // Check if tooltip would be clipped horizontally
      const padding = 10;
      if (idealLeft < padding || idealLeft + tooltipRect.width > viewportWidth - padding) {
        needsCustomPositioning = true;
        let leftOffset = Math.max(padding, Math.min(idealLeft, viewportWidth - tooltipRect.width - padding));
        
        style.position = 'fixed';
        style.left = `${leftOffset}px`;
        style.transform = 'none';
        style.width = 'max-content';
        style.maxWidth = `${Math.min(400, viewportWidth - 2 * padding)}px`;
        
        // Use proper spacing from trigger
        if (newPosition === 'top') {
          style.bottom = `${viewportHeight - triggerRect.top + 8}px`;
        } else {
          style.top = `${triggerRect.bottom + 8}px`;
        }
      }
    }

    // Only apply custom positioning if tooltip would be clipped vertically
    if (newPosition === 'left' || newPosition === 'right') {
      const triggerCenter = triggerRect.top + triggerRect.height / 2;
      const tooltipHalfHeight = tooltipRect.height / 2;
      const idealTop = triggerCenter - tooltipHalfHeight;
      
      // Check if tooltip would be clipped vertically
      const padding = 10;
      if (idealTop < padding || idealTop + tooltipRect.height > viewportHeight - padding) {
        needsCustomPositioning = true;
        let topOffset = Math.max(padding, Math.min(idealTop, viewportHeight - tooltipRect.height - padding));
        
        style.position = 'fixed';
        style.top = `${topOffset}px`;
        style.transform = 'none';
        style.width = 'max-content';
        style.maxWidth = `${Math.min(400, viewportWidth - 2 * padding)}px`;
        
        // Use proper spacing from trigger
        if (newPosition === 'left') {
          style.right = `${viewportWidth - triggerRect.left + 8}px`;
        } else {
          style.left = `${triggerRect.right + 8}px`;
        }
      }
    }

    setAdjustedPosition(newPosition);
    setPositionStyle(needsCustomPositioning ? style : {});
    setIsPositioned(true);
  }, [position, isPositioned]);

  // Adjust position when tooltip becomes visible
  useEffect(() => {
    if ((isVisible || clickVisible) && tooltipRef.current && triggerRef.current) {
      // Longer delay to ensure tooltip is fully rendered with content
      const timer = setTimeout(() => {
        calculatePosition();
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [isVisible, clickVisible, calculatePosition]);

  // Handle click outside to close tooltip
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        tooltipRef.current &&
        triggerRef.current &&
        !tooltipRef.current.contains(event.target as Node) &&
        !triggerRef.current.contains(event.target as Node)
      ) {
        setClickVisible(false);
        setIsPositioned(false);
      }
    };

    if (clickVisible) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [clickVisible]);

  const handleMouseEnter = () => {
    if (trigger === 'hover' || trigger === 'both') {
      setIsVisible(true);
      setIsPositioned(false);
    }
  };

  const handleMouseLeave = () => {
    if (trigger === 'hover' || trigger === 'both') {
      setIsVisible(false);
      setIsPositioned(false);
    }
  };

  const handleClick = (e: React.MouseEvent) => {
    if (trigger === 'click' || trigger === 'both') {
      e.preventDefault();
      e.stopPropagation();
      setClickVisible(!clickVisible);
      setIsPositioned(false);
    }
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    if (trigger === 'click' || trigger === 'both') {
      e.preventDefault();
      setClickVisible(!clickVisible);
      setIsPositioned(false);
    }
  };

  // Show tooltip if either hover or click is active
  const shouldShowTooltip = isVisible || clickVisible;

  return (
    <div className={`tooltip-container ${className}`}>
      <div
        ref={triggerRef}
        className="tooltip-trigger"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onClick={handleClick}
        onTouchStart={handleTouchStart}
        role="button"
        tabIndex={0}
        aria-describedby={shouldShowTooltip ? 'tooltip-content' : undefined}
      >
        {children}
      </div>
      
      {shouldShowTooltip && (
        <div
          ref={tooltipRef}
          id="tooltip-content"
          className={`tooltip-content tooltip-${adjustedPosition}`}
          role="tooltip"
          style={positionStyle}
          dangerouslySetInnerHTML={{ __html: content }}
        />
      )}
    </div>
  );
};

interface ParameterLabelWithTooltipProps {
  label: string;
  description: string;
  className?: string;
  htmlFor?: string;
}

/**
 * Specialized component for parameter labels with tooltips
 */
export const ParameterLabelWithTooltip: React.FC<ParameterLabelWithTooltipProps> = ({
  label,
  description,
  className = '',
  htmlFor
}) => {
  return (
    <Tooltip content={description} className={`parameter-label-tooltip ${className}`}>
      <label htmlFor={htmlFor} className="parameter-label">
        {label}:
      </label>
    </Tooltip>
  );
};
